import type React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  listBom,
  listAdminProducts,
  addBomItem,
  updateBomItem,
  deleteBomItem,
  type AdminBomItem,
  type AdminProduct,
} from '../../lib/adminApi';
import { formatCents } from '../../lib/format';
import { cls } from './shared';

export function BomTab() {
  const { id } = useParams<{ id: string }>();
  const layoutId = parseInt(id!, 10);
  const [items, setItems] = useState<AdminBomItem[]>([]);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addProductId, setAddProductId] = useState<number | null>(null);
  const [addQty, setAddQty] = useState(1);
  const [adding, setAdding] = useState(false);

  function load() {
    Promise.all([listBom(layoutId), listAdminProducts()])
      .then(([br, pr]) => {
        setItems(br.items);
        setProducts(pr.products);
        if (pr.products.length > 0 && !addProductId) {
          setAddProductId(pr.products[0]!.id);
        }
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(load, [layoutId]);

  const total = items.reduce((sum, i) => sum + i.lineTotalCents, 0);

  async function handleAdd() {
    if (!addProductId) return;
    setAdding(true);
    try {
      await addBomItem(layoutId, { product_id: addProductId, quantity: addQty });
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to add');
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(productId: number) {
    if (!confirm('Remove this item from the BOM?')) return;
    try {
      await deleteBomItem(layoutId, productId);
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to remove');
    }
  }

  if (loading) return <p className="text-vari-gray text-sm">Loading…</p>;
  if (error) return <p className="text-red-600 text-sm">{error}</p>;

  const usedProductIds = new Set(items.map((i) => i.productId));
  const availableProducts = products.filter((p) => !usedProductIds.has(p.id));

  return (
    <>
      {availableProducts.length > 0 && (
        <div className={cls.formPanel}>
          <p className="text-sm font-medium text-vari-navy mb-3">Add Product</p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-xs text-vari-gray block mb-1">Product</label>
              <select
                className={cls.select + ' w-full'}
                value={addProductId ?? ''}
                onChange={(e) => setAddProductId(parseInt(e.target.value, 10))}
              >
                {availableProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.sku} — {p.name} ({formatCents(p.unitPriceCents)})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Qty</label>
              <input
                type="number"
                min={1}
                className="border border-vari-soft-gray rounded-md px-3 py-1.5 text-sm w-20 focus:outline focus:outline-1 focus:outline-vari-navy"
                value={addQty}
                onChange={(e) => setAddQty(parseInt(e.target.value, 10) || 1)}
              />
            </div>
            <button
              onClick={() => void handleAdd()}
              disabled={adding}
              className={cls.btnSmPrimary}
            >
              {adding ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      <div className={cls.tableWrap}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-vari-soft-gray">
            <tr>
              <th className={cls.th}>SKU</th>
              <th className={cls.th}>Name</th>
              <th className={cls.thRight}>Unit Price</th>
              <th className={cls.thCenter}>Qty</th>
              <th className={cls.thRight}>Line Total</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <BomRow
                key={item.id}
                item={item}
                striped={i % 2 === 1}
                layoutId={layoutId}
                onDelete={() => void handleDelete(item.productId)}
                onSaved={load}
              />
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-vari-gray text-sm">
                  No products in BOM.
                </td>
              </tr>
            )}
          </tbody>
          {items.length > 0 && (
            <tfoot className="border-t-2 border-vari-soft-gray">
              <tr>
                <td colSpan={4} className="px-4 py-3 text-sm font-medium text-right text-vari-navy">
                  Total
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-right text-vari-navy">
                  {formatCents(total)}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}

function BomRow({
  item,
  striped,
  layoutId,
  onDelete,
  onSaved,
}: {
  item: AdminBomItem;
  striped: boolean;
  layoutId: number;
  onDelete: () => void;
  onSaved: () => void;
}) {
  const [editQty, setEditQty] = useState(item.quantity);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await updateBomItem(layoutId, item.productId, { quantity: editQty });
      setEditing(false);
      onSaved();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr className={cls.tr(striped)}>
      <td className={cls.tdMono}>{item.sku}</td>
      <td className={cls.td}>{item.name}</td>
      <td className={`${cls.tdRight} text-vari-gray`}>{formatCents(item.unitPriceCents)}</td>
      <td className="px-4 py-3 text-sm text-center">
        {editing ? (
          <input
            type="number"
            min={1}
            className="border border-vari-soft-gray rounded px-1 py-0.5 text-sm w-16 text-center"
            value={editQty}
            onChange={(e) => setEditQty(parseInt(e.target.value, 10) || 1)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void save();
              if (e.key === 'Escape') setEditing(false);
            }}
            autoFocus
          />
        ) : (
          <span
            className="cursor-pointer hover:text-vari-navy hover:underline"
            onClick={() => setEditing(true)}
          >
            {item.quantity}
          </span>
        )}
      </td>
      <td className={`${cls.tdRight} font-medium`}>{formatCents(item.lineTotalCents)}</td>
      <td className={`${cls.tdRight} space-x-2`}>
        {editing ? (
          <>
            <button
              onClick={() => void save()}
              disabled={saving}
              className={cls.btnSmPrimary}
            >
              {saving ? '…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className={cls.btnGhost}>
              Cancel
            </button>
          </>
        ) : (
          <button onClick={onDelete} className={cls.btnDanger}>
            Remove
          </button>
        )}
      </td>
    </tr>
  );
}
